import {
  backdropAtom,
  planDestListAtom,
  planRegistConfirmAtom,
  selectedOrderListAtom,
  selectedPlanCarListAtom,
  targetShippingSeqAtom,
} from '@/commons/constants';
import { planShippingListAtom, postShipping } from '@/interfaces/plan/shipping';
import { PlanCar, PlanDest } from '@/types/plan';
import { Button, Divider, Modal, Typography } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import styled from 'styled-components';
import { GridCloseIcon } from '@mui/x-data-grid';
import { CustomTextField } from '../common/CustomMui';
import { execModalAtom } from '../common/constants';
import CustomDatePicker from '@/commons/CustomDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';

const ModalWrap = styled.div`
  position: absolute;
  top:50%;
  left:50%;
  transform: translate(-50%, -50%);
  width: 25%;
  height: 33rem;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  .head{
    padding: 0.5rem;
    width: 100%;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background:rgba(82, 133, 164, 0.87);
    color: #ffffff;
  }
  .body{
    padding: 1rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 1rem;
  }
  .button{
    padding: 0 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }
`;

export interface ConfirmModalProps {
  name: string;
  date: string;
  center_seq: number;
  center_name: string;
  option: {
    shipping_type: number;
    level: number;
    d_mode: string;
  };
  dest_list: PlanDest[];
  car_list: PlanCar[];
}

export default function ConfirmModal({ target }: { target: ConfirmModalProps }) {
  const setBackdrop = useSetAtom(backdropAtom);
  const setExecModal = useSetAtom(execModalAtom);
  const { refetch: listRefetch } = useAtomValue(planShippingListAtom);
  const [planRegistConfirmModal, setPlanRegistConfirmModal] = useAtom(planRegistConfirmAtom);
  const setSelectedPlanDestList = useSetAtom(planDestListAtom);
  const setSelectedPlanCarList = useSetAtom(selectedPlanCarListAtom);
  const setSelectedOrderList = useSetAtom(selectedOrderListAtom);
  const setTargetShippingSeq = useSetAtom(targetShippingSeqAtom);

  const [shippingName, setShippingName] = useState<string>(target?.name);
  const [deliveryDate, setDeliveryDate] = useState<string>(dayjs(target?.date).format('YYYY/MM/DD'));

  useEffect(() => {
    if (target) {
      setShippingName(target.name);
      setDeliveryDate(dayjs(target?.date).format('YYYY/MM/DD'));
    }
  }, [target]);

  const handleClickConfirm = async () => {
    setBackdrop(true);
    try {
      const ret = await postShipping({
        name: shippingName,
        date: deliveryDate,
        center_seq: target.center_seq,
        center_name: target.center_name,
        option: target.option,
        dest_list: target.dest_list,
        car_list: target.car_list,
      });

      setBackdrop(false);
      setPlanRegistConfirmModal(false);
      if (ret && ret.data.data.t_shipping_seq) {
        listRefetch();
        setTargetShippingSeq(ret.data.data.t_shipping_seq);
        setExecModal(false);
        setSelectedOrderList([]);
        setSelectedPlanDestList([]);
        setSelectedPlanCarList([]);
      } else if (ret.data.err && ret.data.err === 'VRP_ZERO') {
        alert(ret.data.data);
      } else {
        alert('예기치 않은 오류로 배차에 실패하였습니다.');
      }
    } catch (e) {
      setBackdrop(false);
      setPlanRegistConfirmModal(false);
      alert('서버와의 연결이 끊어졌습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  const handleClose = () => {
    setPlanRegistConfirmModal(false);
  };

  const handleDatePickerChange = (value: Dayjs) => {
    setDeliveryDate(value?.format('YYYY-MM-DD'));
  };

  return (
    <Modal open={planRegistConfirmModal} onClose={handleClose}>
      <ModalWrap>
        {planRegistConfirmModal === true && (
          <>
            <div className="head">
              <Typography variant="h5">배차 실행</Typography>
              <GridCloseIcon onClick={handleClose} />
            </div>
            <Divider />
            <div className="body">
              <CustomTextField
                id="outlined-basic"
                label="이름"
                fullWidth
                variant="outlined"
                value={shippingName}
                onChange={(e) => setShippingName(e.target.value)}
              />
              <CustomDatePicker label="배송일" onChange={handleDatePickerChange} defaultValue={dayjs(target.date)} />
              <CustomTextField
                id="outlined-basic"
                label="배차옵션"
                fullWidth
                variant="outlined"
                disabled
                value={
                  (target.option.shipping_type === 0 || target.option.shipping_type === 2 ? '최소차량 ' : '모든차량 ') +
                  (target.option.shipping_type === 1 || target.option.shipping_type === 2
                    ? '행정구역 ' + (target.option.level === 1 ? '시/도' : '구/군')
                    : '')
                }
              />
              <CustomTextField
                id="outlined-basic"
                label="배송지"
                fullWidth
                variant="outlined"
                disabled
                value={target.dest_list[0].name + '외 ' + (target.dest_list.length - 1) + '개'}
              />
              <CustomTextField
                id="outlined-basic"
                label="차량"
                fullWidth
                variant="outlined"
                disabled
                value={target.car_list.length + '대'}
              />
              <Typography>이 내용으로 배차를 실행하시겠습니까?</Typography>
            </div>
            <div className="button">
              <Button variant="contained" fullWidth color="warning" onClick={handleClose}>
                취소
              </Button>
              <Button variant="contained" fullWidth onClick={handleClickConfirm}>
                실행
              </Button>
            </div>
          </>
        )}
      </ModalWrap>
    </Modal>
  );
}
